// Libraries
import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { isBefore } from 'date-fns';

// Interfaces
import { Fragment } from 'src/app/interfaces/api/fragment';
import { Metric } from 'src/app/interfaces/api/metric';
import { Planning } from 'src/app/interfaces/api/planning';
import { defaultTask, PlanningTask } from 'src/app/interfaces/api/task';
import { PlanningDisplayModel } from 'src/app/interfaces/shared/planning-display';

@Injectable({ providedIn: 'root' })
export class PlanningStore {
  // Variables
  loaded: boolean = false;

  // Signals
  readonly planning: WritableSignal<Planning> = signal<Planning>({} as Planning);
  readonly tasks: WritableSignal<PlanningTask[]> = signal<PlanningTask[]>([{ ...defaultTask }]);
  readonly metrics: WritableSignal<Metric[]> = signal<Metric[]>([]);
  readonly fragments: WritableSignal<Fragment[]> = signal<Fragment[]>([]);

  private readonly _selectedDay: WritableSignal<string> = signal<string>("");
  public readonly selectedDay: Signal<string> = computed(() => this._selectedDay());

  isolatedMetrics: WritableSignal<Metric[]> = signal<Metric[]>([]);
  isolatedFragments: WritableSignal<Fragment[]> = signal<Fragment[]>([]);

  //user action change the selection
  selectedMetrics: WritableSignal<Metric[]> = signal<Metric[]>([]);
  readonly selectedFragments: WritableSignal<Fragment[]> = signal<Fragment[]>([]);
  selectedTasks: WritableSignal<PlanningTask[]> = signal<PlanningTask[]>([]);

  //system set up 
  currentDate: WritableSignal<string | null> = signal<string | null>(null);
  currentTask: WritableSignal<PlanningTask | null> = signal<PlanningTask | null>(null);
  displayMetrics: WritableSignal<Metric[]> = signal<Metric[]>([]);

  //loading states
  readonly isLoadingTasks: WritableSignal<boolean> = signal<boolean>(true);
  readonly isLoadingMetrics: WritableSignal<boolean> = signal<boolean>(true);
  readonly isLoadingFragments: WritableSignal<boolean> = signal<boolean>(true);
  readonly isLoadingModel: WritableSignal<boolean> = signal<boolean>(true);

  metricsByTaskIds = signal<Record<string, Metric[]>>({});
  fragmentsByTaskIds = signal<Record<string, Fragment[]>>({});
  taskByFragmentIds = signal<Record<string, PlanningTask>>({});

  displayModel: WritableSignal<PlanningDisplayModel> = signal<PlanningDisplayModel>({
    done: true,
    todo: true,
    progress: true,
    late: true,
    ahead: true,
    unused: true,
  });

  displayMetric: WritableSignal<Record<string, boolean>> = signal({
    concreting: true,
    formwork: true,
    reinforcement: true,
    precast_slab: true,
    beam: true,
    wall: true,
    column: true,
    other: true,
  });

  // Computeds
  tasksOrder = computed(() => this.tasks().map(t => t.number));

  fragmentByDbIds = computed(() =>
    Object.fromEntries(this.fragments().map(f => [f.dbId, f]))
  );
  fragmentByIds = computed(() =>
    Object.fromEntries(this.fragments().map(f => [f.id, f]))
  );
  metricByIds = computed(() =>
    Object.fromEntries(this.metrics().map(f => [f.id, f]))
  );
  taskByIds: Signal<{ [k: string]: PlanningTask; }> = computed(() =>
    Object.fromEntries(this.tasks().map(f => [f.id, f]))
  );

  readonly sortedTasks = computed(() => {
    const tasks = this.tasks();
    const order = this.planning().tasksOrder ?? [];

    if (!order.length) return tasks;

    const rank = new Map(order.map((n, i) => [n, i]));

    return [...tasks].sort((a, b) => {
      const ra = rank.get(a.number) ?? Number.MAX_SAFE_INTEGER;
      const rb = rank.get(b.number) ?? Number.MAX_SAFE_INTEGER;
      return ra - rb;
    });
  });

  readonly showUnlinked = computed(() => this.planning().showUnlinked ?? false);

  constructor() { }

  onDataLoaded() {
    const groupedMetric: Record<string, Metric[]> = {};
    const groupedFragment: Record<string, Fragment[]> = {};
    const taskByFragment: Record<string, PlanningTask> = {};

    for (const metric of this.metrics()) {
      const taskId = metric.payload['taskId'];

      if (!taskId) continue;

      (groupedMetric[taskId] ??= []).push(metric);
    }

    for (const task of this.tasks()) {
      if (!task.fragments) continue;

      (task.fragments ??= []).forEach(f => {
        taskByFragment[f] = task;
      })
    }

    for (const fragment of this.fragments()) {
      const taskId = fragment.task;

      if (!taskId) continue;

      (groupedFragment[taskId] ??= []).push(fragment);
    }

    // 🔹 Tri une seule fois par taskId
    for (const taskId in groupedMetric) {
      groupedMetric[taskId]!.sort(
        (a, b) =>
          new Date(a.createdDate).getTime() -
          new Date(b.createdDate).getTime()
      );
    }

    this.metricsByTaskIds.set(groupedMetric);
    this.fragmentsByTaskIds.set(groupedFragment);
    this.taskByFragmentIds.set(taskByFragment);

    this.tasks.update(tasks =>
      tasks.map(t => {
        const metrics = groupedMetric[t.id] ?? [];
        return {
          ...t,
          children: t.children ?? [],
          metrics,
          fragments: t.fragments ?? [],
          indent: t.indent ?? 0,
          firstUpdate: metrics[0]?.createdDate ?? '',
          lastUpdate: metrics.at(-1)?.createdDate ?? ''
        };
      })
    );

    this.fragments.update(fragments =>
      fragments.map(f => {
        if (f.task) {
          const task = this.taskByIds()[f.task];

          if (task) {
            f.aheadDate = task.startDate;
            f.dueDate = task.endDate;
          }
        }

        if (!f.aheadDate) {
          f.aheadDate = new Date().toISOString()
        }

        return {
          ...f
        };
      }).sort((a, b) => a.aheadDate && b.aheadDate && isBefore(a.aheadDate, b.aheadDate) ? -1 : 1)
    )
  };

  sort(tasks: PlanningTask[]): PlanningTask[] {
    const map = new Map<string, PlanningTask>();
    const pointed = new Set<string>();

    // indexation
    for (const t of tasks) {
      map.set(t.uid || t.id, t);
      if (t.target) {
        pointed.add(t.target);
      }
    }
    // trouver les "têtes" (non pointées)
    const heads = tasks.filter(t => !pointed.has(t.uid || t.id));

    const result: PlanningTask[] = [];

    function walk(start: PlanningTask) {
      let current: PlanningTask | undefined = start;

      while (current) {
        result.push(current);
        current = current.target ? map.get(current.target || "0") : undefined;
      }
    }

    // parcourir chaque chaîne
    for (const head of heads) {
      walk(head);
    }

    return result;
    // 1. Groupement
    // const groups = new Map<number | null, PlanningTask[]>();

    // for (const task of tasks) {
    //   const key = task.parent ?? null;
    //   if (!groups.has(key as number)) groups.set(key as number, []);
    //   groups.get(key as number)!.push(task);
    // }

    // // 2. Tri des clés de groupes : null (racines) en premier, puis ASC
    // const sortedKeys = [...groups.keys()].sort((a, b) => {
    //   if (a === null) return -1;
    //   if (b === null) return 1;
    //   return a - b;
    // });

    // // 3. Aplatissement : chaque groupe est trié par num ASC
    // return sortedKeys.flatMap(key =>
    //   groups.get(key)!.sort((a, b) => a.number - b.number)
    // );
  }

  /**
   * Setters
   */
  setCurrentDate(date: string) {
    this.currentDate.set(date);
  }

  setCurrentTask(task: PlanningTask | null) {
    this.currentTask.set(task);
  }

  // setCurrentMetric(metric: Metric) {
  //   this.currentMetric.set(metric);
  // }

  setTasks(tasks: PlanningTask[]) {
    this.tasks.set(tasks);
  }

  setFragments(fragments: Fragment[]) {
    this.fragments.set(fragments);
  }

  setDisplayModel(displayModel: Partial<PlanningDisplayModel>) {
    this.displayModel.update(current => ({
      ...current,
      ...displayModel
    }));
  }

  setDisplayMetric(displayMetric: Partial<Record<string, boolean>>) {
    this.displayMetric.update(current => ({
      ...current,
      ...displayMetric
    }) as Record<string, boolean>);
  }

  setSelectedDay(dayIds: string) {
    this._selectedDay.set(dayIds);
  }

  /**
   * Fragments getters
   */
  getFragmentsAtDate(): Fragment[] {
    return this.fragments().filter(f => f.aheadDate && isBefore(f.aheadDate, this.currentDate() ?? ''))
  }

  getFragmentsByIds(ids: string[]) {
    let fragments = [];

    for (let id of ids) {
      let found = this.fragmentByIds()[id];

      if (found)
        fragments.push(found)
    }

    return fragments;
  }

  selectFragments(fragments: Fragment[]) {
    this.selectedFragments.set(fragments)
  }

  /**
   * Metrics getters
   */
  getMetricsAtDate(): Metric[] {
    return this.metrics().filter(m => m.payload?.startDate && isBefore(m.payload?.startDate, this.currentDate() ?? ''))
  }

  getMetricsByIds(ids: string[]) {
    let metrics = [];

    for (let id of ids) {
      let found = this.metricByIds()[id];

      if (found)
        metrics.push(found)
    }

    return metrics;
  }

  selectMetrics(metrics: Metric[]) {
    this.selectedMetrics.set(metrics)
  }


  isolateMetrics(metrics: Metric[]) {
    this.isolatedMetrics.set(metrics)
  }

  isolateFragments(fragments: Fragment[]) {
    this.isolatedFragments.set(fragments)
  }

  /**
   * Tasks getters
   */
  getTasksAtDate(): PlanningTask[] {
    return this.tasks().filter(t => t.startDate && isBefore(t.startDate, this.currentDate() ?? ''))
  }

  getTaskById(id: string) {
    return this.getTasksByIds([id])[0];
  }

  getTasksByIds(ids: string[]) {
    let tasks = [];

    for (let id of ids) {
      let found = this.taskByIds()[id];

      if (found)
        tasks.push(found)
    }

    return tasks;
  }

  getFragmentTask(fragIds: string[]) {
    return this.tasks().filter(t => t.fragments?.find(f => fragIds.includes(f)));
  }
  selectTasks(tasks: PlanningTask[]) {
    this.selectedTasks.set(tasks)
  }

  // CRUD Tasks
  addTask(task: PlanningTask) {
    this.tasks.update(tasks => [...tasks, task]);
  }

  updateTasks(tasks: PlanningTask[]): void {
    this.tasks.set(tasks);
  }

  updateTask(task: PlanningTask): void {
    this.tasks.update(tasks => {
      const index = tasks.findIndex(t => t.id === task.id);

      if (index >= 0) {
        const updated = [...tasks];
        updated[index] = task;
        return updated;
      }

      return [...tasks, task];
    });
  }


  removeTask(taskId: string): void {
    const index = this.tasks().findIndex(task => task.id === taskId);

    if (index === -1) return;

    let task = this.tasks()[index];

    if (task) {
      if (task.parent) {
        let parentTask = this.tasks().find(task => task.number == task.parent);

        if (parentTask && parentTask.children) {
          parentTask.children = parentTask.children.filter(child => child.id !== taskId);
        }
      }

      if (task.number == this.currentTask()?.number) {
        this.currentTask.set(null);
      }

      this.tasks.update(tasks => tasks.filter(t => t.id !== taskId));
    }
  }

  updateFragments(frags: Fragment[]): void {
    this.fragments.set(frags);
  }

  updateMetrics(metrics: Metric[]): void {
    this.metrics.set(metrics);
  }

  addSelectedMetric(metric: Metric) {
    this.selectedMetrics.update(metrics => [...metrics, metric]);
  }

  updatePlanning(planning: Planning): void {
    this.planning.set(planning);
  }

  isLoading(type: 'task' | 'metric' | 'fragment' | 'model', loading: boolean = false) {
    switch (type) {
      case 'task':
        this.isLoadingTasks.set(loading);
        break;

      case 'metric':
        this.isLoadingMetrics.set(loading);
        break;

      case 'fragment':
        this.isLoadingFragments.set(loading);
        break;

      case 'model':
        this.isLoadingModel.set(loading);
        break;

      default:
        this.isLoadingTasks.set(loading);
        this.isLoadingMetrics.set(loading);
        this.isLoadingFragments.set(loading);
        this.isLoadingModel.set(loading);
        break;
    }
  }

  reset() {
    this.loaded = false;
    this.isLoadingTasks.set(true);
    this.isLoadingMetrics.set(true);
    this.isLoadingFragments.set(true);
    this.isLoadingModel.set(true);
    this.planning.set({} as Planning);
    this.tasks.set([]);
    this.metrics.set([]);
    this.fragments.set([]);
    this.currentTask.set(null);
    this.selectedMetrics.set([]);
    this.selectedFragments.set([]);
    this.selectedTasks.set([]);
    this._selectedDay.set("");
    this.currentDate.set(new Date().toISOString());
  }
}
